import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { datajs, datajsAll, getMapStyle, CustomClusterRenderer, getMapIcon, getMapContentHTML, getActiveMapIcon } from '../shared/utils';
import { Tab, Dropdown } from 'bootstrap';
const google = window.google;

export default class CaseArchive {
	static getEmptyState() {
		return { areas: [], locations: [], sectors: [] };
	}

	static init() {
		const archive = datajs('case-archive');
		const locationFilterWrapper = datajs('location-filter-wrapper');
		const areaFilterWrapper = datajs('area-filter-wrapper');
		const sectorFilterWrapper = datajs('sector-filter-wrapper');

		if (!google) {
			return;
		}

		// Make sure all required components exists
		if (!archive || !locationFilterWrapper || !areaFilterWrapper || !sectorFilterWrapper) {
			return;
		}

		// Store the initial state for the popstate event later
		const initialFilterState = this.getInitialFilterState();

		// Apply the initial filter state to the currentFilterState variable
		CaseArchive.currentFilterState = this.getInitialFilterState();

		const updateCurrentFilterState = (e, filterType) => {
			// The updated filter value
			const filterValue = e.target.value;
			const filterText = e.target.closest('li').querySelector('label').innerHTML;

			// Add this filter if 'checked' and not included in the current filter state already
			if (e.target.checked && !CaseArchive.currentFilterState[filterType].includes(filterValue)) {
				CaseArchive.currentFilterState[filterType].push(filterValue);
				this.appendToSelectedFiltersList({
					type: filterType,
					value: filterValue,
					text: filterText,
				});
			}

			// Else, remove this filter from the current filter state
			else {
				CaseArchive.currentFilterState[filterType] = CaseArchive.currentFilterState[filterType].filter((value) => {
					return value !== filterValue;
				});
				this.removeFromSelectedFiltersList({
					type: filterType,
					value: filterValue,
				});
			}

			// Update the url
			if ('URLSearchParams' in window) {
				var searchParams = new URLSearchParams(window.location.search);
				searchParams.set('lc', CaseArchive.currentFilterState.locations);
				searchParams.set('ar', CaseArchive.currentFilterState.areas);
				searchParams.set('se', CaseArchive.currentFilterState.sectors);
				var newRelativePathQuery = window.location.pathname + '?' + searchParams.toString() + window.location.hash;
				history.pushState(CaseArchive.currentFilterState, '', newRelativePathQuery);
			}

			// Apply the new filter state
			this.applyFilterState(CaseArchive.currentFilterState);
		};

		// Area filter
		if (areaFilterWrapper) {
			const areaFilterToggle = datajs('dropdown-toggle', areaFilterWrapper);
			const areaFilterItems = datajsAll('area-filter-item', areaFilterWrapper);
			new Dropdown(areaFilterToggle, { autoClose: 'outside' });

			areaFilterItems.forEach((filterItem) => {
				// Update 'type' filter on click
				filterItem.addEventListener('click', function (e) {
					updateCurrentFilterState(e, 'areas');
				});
			});
		}

		// Location filter
		if (locationFilterWrapper) {
			const locationFilterToggle = datajs('dropdown-toggle', locationFilterWrapper);
			const locationFilterItems = datajsAll('location-filter-item', locationFilterWrapper);
			new Dropdown(locationFilterToggle, { autoClose: 'outside' });

			locationFilterItems.forEach((filterItem) => {
				// Update 'location' filter on click
				filterItem.addEventListener('click', function (e) {
					updateCurrentFilterState(e, 'locations');
				});
			});
		}

		// Sector filter
		if (sectorFilterWrapper) {
			const sectorFilterToggle = datajs('dropdown-toggle', sectorFilterWrapper);
			const sectorFilterItems = datajsAll('sector-filter-item', sectorFilterWrapper);
			new Dropdown(sectorFilterToggle, { autoClose: 'outside' });

			sectorFilterItems.forEach((filterItem) => {
				// Update 'sector' filter on click
				filterItem.addEventListener('click', function (e) {
					updateCurrentFilterState(e, 'sectors');
				});
			});
		}

		// Init the map
		CaseArchive.map = new google.maps.Map(CaseArchive.mapElement, {
			zoom: 14,
			disableDefaultUI: true,
			zoomControl: true,
			zoomControlOptions: {
				position: google.maps.ControlPosition.RIGHT_TOP,
			},
			styles: getMapStyle(),
		});

		// Give the infowindow some initial content
		CaseArchive.infowindow = new google.maps.InfoWindow({
			content: 'Loading...',
			disableAutoPan: true,
		});

		// Reset icon on close
		google.maps.event.addListener(CaseArchive.infowindow, 'closeclick', () => {
			CaseArchive.currentMarker.setIcon(getMapIcon('case'));
		});

		// Kick things off initially
		this.applyFilterState(CaseArchive.currentFilterState);
		this.bindEvents();

		// Enable back button
		window.addEventListener('popstate', (e) => {
			const locationFilterWrapper = datajs('location-filter-wrapper');
			const areaFilterWrapper = datajs('area-filter-wrapper');
			const locationFilterItems = datajsAll('location-filter-item', locationFilterWrapper);
			const areaFilterItems = datajsAll('area-filter-item', areaFilterWrapper);
			const sectorFilterWrapper = datajs('sector-filter-wrapper');
			const sectorFilterItems = datajsAll('sector-filter-item', sectorFilterWrapper);

			let state = e.state;

			// Empty selected filters list
			datajs('selected-filters-list').innerHTML = '';

			// When there is no previous state
			if (!state) {
				const searchParams = new URLSearchParams(window.location.search);

				// If there were any params initialy, use the initial filter state
				if (searchParams.get('lc') || searchParams.get('ar') || searchParams.get('se')) {
					state = initialFilterState;
				} else {
					// Else use an empty state
					state = { areas: [], locations: [], sectors: [] };
				}
			}

			if (state?.locations) {
				locationFilterItems.forEach((filterItem) => {
					if (state.locations.includes(filterItem.value)) {
						filterItem.checked = true;
						this.appendToSelectedFiltersList({
							type: 'locations',
							value: filterItem.value,
							text: filterItem.closest('li').querySelector('label').innerHTML,
						});
					} else {
						filterItem.checked = false;
					}
				});
			}

			if (state?.areas) {
				areaFilterItems.forEach((filterItem) => {
					if (state.areas.includes(filterItem.value)) {
						filterItem.checked = true;
						this.appendToSelectedFiltersList({
							type: 'areas',
							value: filterItem.value,
							text: filterItem.closest('li').querySelector('label').innerHTML,
						});
					} else {
						filterItem.checked = false;
					}
				});
			}

			if (state?.sectors) {
				sectorFilterItems.forEach((filterItem) => {
					if (state.sectors.includes(filterItem.value)) {
						filterItem.checked = true;
						this.appendToSelectedFiltersList({
							type: 'sectors',
							value: filterItem.value,
							text: filterItem.closest('li').querySelector('label').innerHTML,
						});
					} else {
						filterItem.checked = false;
					}
				});
			}

			CaseArchive.currentFilterState = state;
			this.applyFilterState(CaseArchive.currentFilterState);
		});
	}

	static bindEvents() {
		/*
		datajs('show-map-view-button').addEventListener('click', () => {
			document.body.classList.add('has-visible-map');
			this.applyFilterState(EstatesMap.currentFilterState);
		});
		*/

		// Switch between list and map view
		const viewToggle = datajs('case-archive-view-toggle');
		if (viewToggle) {
			const triggerTabList = viewToggle.querySelectorAll('button');
			triggerTabList.forEach((triggerEl) => {
				const tabTrigger = new Tab(triggerEl);

				triggerEl.addEventListener('click', (event) => {
					event.preventDefault();
					tabTrigger.show();
				});
			});
		}

		// When map tab is shown
		const mapTabEl = document.querySelector('button[data-bs-target="#case-archive-map"]');
		mapTabEl.addEventListener('shown.bs.tab', () => {
			this.drawMarkers();
		});

		datajsAll('clear-filter-button').forEach((item) => {
			item.addEventListener('click', () => {
				CaseArchive.currentFilterState = this.getEmptyState();

				// Update the url
				if ('URLSearchParams' in window) {
					var searchParams = new URLSearchParams(window.location.search);
					searchParams.delete('lc');
					searchParams.delete('ar');
					searchParams.delete('se');
					var newRelativePathQuery = window.location.pathname + window.location.hash;
					history.pushState(CaseArchive.currentFilterState, '', newRelativePathQuery);
				}


				this.applyFilterState(CaseArchive.currentFilterState);
				this.uncheckFilterCheckboxes();
			});
		});

		// Remove specific filters when clicking on their labels outside the checkbox dropdowns
		datajs('selected-filters-list').addEventListener('click', (e) => {
			const { target } = e;

			if (!target.matches('button')) {
				return false;
			}

			const type = target.dataset.type;
			const removeValue = target.dataset.value;
			const locationFilterWrapper = datajs('location-filter-wrapper');
			const areaFilterWrapper = datajs('area-filter-wrapper');
			const sectorFilterWrapper = datajs('sector-filter-wrapper');

			// Area filter
			if (type === 'areas' && areaFilterWrapper) {
				const areaFilterItems = datajsAll('area-filter-item', areaFilterWrapper);

				areaFilterItems.forEach((filterItem) => {
					if (filterItem.value === removeValue) {
						filterItem.click();
					}
				});
			}

			// Location filter
			if (type === 'locations' && locationFilterWrapper) {
				const locationFilterItems = datajsAll('location-filter-item', locationFilterWrapper);

				locationFilterItems.forEach((filterItem) => {
					if (filterItem.value === removeValue) {
						filterItem.click();
					}
				});
			}

			// Sector filter
			if (type === 'sectors' && sectorFilterWrapper) {
				const sectorFilterItems = datajsAll('sector-filter-item', sectorFilterWrapper);

				sectorFilterItems.forEach((filterItem) => {
					if (filterItem.value === removeValue) {
						filterItem.click();
					}
				});
			}
		});

		// Remove map loader
		google.maps.event.addListenerOnce(CaseArchive.map, 'tilesloaded', () => {
			CaseArchive.mapElement.classList.remove('has-loader');
		});
	}

	static deleteMarkers() {
		if (CaseArchive.markers.length) {
			for (let i = 0; i < CaseArchive.markers.length; i++) {
				CaseArchive.markers[i].setMap(null);
			}
			CaseArchive.markers = [];
		}
	}

	static drawMarkers() {
		const boundbox = new google.maps.LatLngBounds();
		const caseItems = datajsAll('case-item');

		// Collection of current visible caseItems
		const visibleCaseItems = caseItems.filter((item) => !item.classList.contains('d-none'));

		// Delete any existing markers
		this.deleteMarkers();

		// Clear any existing clusterer
		if (CaseArchive.markerClusterer) {
			CaseArchive.markerClusterer.clearMarkers();
		}

		visibleCaseItems.forEach((caseItem) => {
			const lat = caseItem.dataset.lat || null;
			const lng = caseItem.dataset.lng || null;
			const contentHtml = getMapContentHTML({ title: caseItem.dataset.title, permalink: caseItem.dataset.permalink });

			if (!lat || !lng) {
				return;
			}

			const marker = new google.maps.Marker({
				position: new google.maps.LatLng(lat, lng),
				icon: getMapIcon('case'),
				map: CaseArchive.map,
				title: caseItem.dataset?.title,
				html: contentHtml,
				cursor: 'pointer',
				animation: google.maps.Animation.DROP,
			});

			CaseArchive.markers.push(marker);

			// Add marker coordinates to bounding box
			boundbox.extend(new google.maps.LatLng(lat, lng));

			// Handle clicks
			google.maps.event.addListener(marker, 'click', () => {
				this.updateCurrentMarker(marker);
			});
		});

		if (CaseArchive.markers.length) {
			CaseArchive.map.setCenter(boundbox.getCenter());

			if (CaseArchive.markers.length === 1) {
				CaseArchive.map.setZoom(17);
			} else {
				CaseArchive.map.fitBounds(boundbox);
			}
		}

		// Add a marker clusterer to manage the markers. Currently same icon for all colors.
		// Add a marker clusterer to manage the markers. Currently same icon for all colors.
		CaseArchive.markerClusterer = new MarkerClusterer({
			map: CaseArchive.map,
			markers: CaseArchive.markers,
			renderer: new CustomClusterRenderer(),
		});
	}

	static updateCurrentMarker(marker) {
		// Reset any existing active marker
		if (CaseArchive.currentMarker) {
			CaseArchive.currentMarker.setIcon(getMapIcon('case'));
		}

		CaseArchive.currentMarker = marker;
		CaseArchive.currentMarker.setIcon(getActiveMapIcon('case'));

		CaseArchive.infowindow.setContent(CaseArchive.currentMarker.html);
		CaseArchive.infowindow.open(CaseArchive.map, CaseArchive.currentMarker);

		CaseArchive.map.panTo(CaseArchive.currentMarker.getPosition());
	}

	static applyFilterState(filterState) {
		const caseItems = datajsAll('case-item');

		// Filter function handlers
		const filterHandlers = new Map([
			[
				'areas',
				(current, areas) =>
					current.some(
						// some - at least one, every - all of them
						(c) => areas.includes(c)
					),
			],
			[
				'locations',
				(current, locations) =>
					current.some(
						// some - at least one, every - all of them
						(c) => locations.includes(c)
					),
			],
			[
				'sectors',
				(current, sectors) =>
					current.some(
						// some - at least one, every - all of them
						(c) => sectors.includes(c)
					),
			],
		]);

		// Filter function
		const filterData = (dataArr, filters) => {
			const filterKeys = Object.keys(filters);
			return dataArr.filter((o) =>
				filterKeys.every((key) => {
					const handler = filterHandlers.get(key);
					return !handler || handler(o.filters[key], filters[key]);
				})
			);
		};

		// Remove any empty filters to prevent the filter function from failing
		let cleanedFilterState = { ...filterState };
		for (const key in cleanedFilterState) {
			if (Array.isArray(cleanedFilterState[key]) && !cleanedFilterState[key].length) delete cleanedFilterState[key];
		}

		// Handle "Clear filter"-button visibiliy
		const totalFilters = (cleanedFilterState?.areas?.length || 0) + (cleanedFilterState?.locations?.length || 0)+ (cleanedFilterState?.sectors?.length || 0);
		if (totalFilters > 0) {
			datajsAll('clear-filter-button').forEach((item) => item.classList.remove('d-none'));
		} else {
			datajsAll('clear-filter-button').forEach((item) => item.classList.add('d-none'));
		}

		// Setup the data array that will be filtered
		let data = [];
		caseItems.forEach((item) => {
			let preparedItem = {
				element: item,
				filters: {
					areas: item.dataset?.areas?.split(',') || [],
					locations: item.dataset?.locations?.split(',') || [],
					sectors: item.dataset?.sectors?.split(',') || [],
				},
			};

			item.classList.add('d-none');
			data.push(preparedItem);
		});

		// Do something with the filtered result
		filterData(data, cleanedFilterState).forEach((result) => {
			result.element.classList.remove('d-none');
		});

		// Update map markers
		this.drawMarkers();

		// Update dropdown toggle text
		this.updateDropdownToggleText();
	}

	static getInitialFilterState() {
		const locationFilterWrapper = datajs('location-filter-wrapper');
		const areaFilterWrapper = datajs('area-filter-wrapper');
		const locationFilterItems = datajsAll('location-filter-item', locationFilterWrapper);
		const areaFilterItems = datajsAll('area-filter-item', areaFilterWrapper);
		const sectorFilterWrapper = datajs('sector-filter-wrapper');
		const sectorFilterItems = datajsAll('sector-filter-item', sectorFilterWrapper);

		let state = this.getEmptyState();

		if (locationFilterItems) {
			locationFilterItems.forEach((filterItem) => {
				if (filterItem.checked) {
					state.locations.push(filterItem.value);
				}
			});
		}

		if (areaFilterItems) {
			areaFilterItems.forEach((filterItem) => {
				if (filterItem.checked) {
					state.areas.push(filterItem.value);
				}
			});
		}

		if (sectorFilterItems) {
			sectorFilterItems.forEach((filterItem) => {
				if (filterItem.checked) {
					state.sectors.push(filterItem.value);
				}
			});
		}

		return state;
	}

	static uncheckFilterCheckboxes() {
		const locationFilterWrapper = datajs('location-filter-wrapper');
		const areaFilterWrapper = datajs('area-filter-wrapper');
		const sectorFilterWrapper = datajs('sector-filter-wrapper');
		const locationFilterItems = datajsAll('location-filter-item', locationFilterWrapper);
		const areaFilterItems = datajsAll('area-filter-item', areaFilterWrapper);
		const sectorFilterItems = datajsAll('sector-filter-item', sectorFilterWrapper);

		if (locationFilterItems) {
			locationFilterItems.forEach((filterItem) => (filterItem.checked = false));
		}

		if (areaFilterItems) {
			areaFilterItems.forEach((filterItem) => (filterItem.checked = false));
		}

		if (sectorFilterItems) {
			sectorFilterItems.forEach((filterItem) => (filterItem.checked = false));
		}

		// Empty selected filters list
		datajs('selected-filters-list').innerHTML = '';

		// Hide clear all filters button
		datajsAll('clear-filter-button').forEach((item) => {
			item.classList.add('d-none');
		});
	}

	static appendToSelectedFiltersList(obj) {
		const selectedFiltersContainer = datajs('selected-filters-list');
		const btn = document.createElement('button');

		btn.setAttribute('data-js', 'selected-filter-item');
		btn.setAttribute('class', 'btn btn-primary');
		btn.dataset.type = obj.type;
		btn.dataset.value = obj.value;
		btn.innerHTML = obj.text;

		selectedFiltersContainer.appendChild(btn);
	}

	static removeFromSelectedFiltersList(obj) {
		const selectedFilterItems = datajsAll('selected-filter-item');

		selectedFilterItems.forEach((item) => {
			if (obj.type === item.dataset.type && obj.value === item.dataset.value) {
				item.parentNode.removeChild(item);
			}
		});
	}

	static updateDropdownToggleText() {
		const locationFilterWrapper = datajs('location-filter-wrapper');
		const areaFilterWrapper = datajs('area-filter-wrapper');
		const sectorFilterWrapper = datajs('sector-filter-wrapper');
		const areasDropdownToggle = datajs('dropdown-toggle', areaFilterWrapper);
		const areasDropdownToggleTextElement = areasDropdownToggle.querySelector('.m-archiveFilters__filter__toggleBtn__count');
		const locationsDropdownToggle = datajs('dropdown-toggle', locationFilterWrapper);
		const locationsDropdownToggleTextElement = locationsDropdownToggle.querySelector('.m-archiveFilters__filter__toggleBtn__count');
		const sectorsDropdownToggle = datajs('dropdown-toggle', sectorFilterWrapper);
		const sectorsDropdownToggleTextElement = sectorsDropdownToggle.querySelector('.m-archiveFilters__filter__toggleBtn__count');

		// Areas
		if (CaseArchive.currentFilterState.areas.length === 0) {
			areasDropdownToggleTextElement.innerHTML = '';
		} else {
			areasDropdownToggleTextElement.innerHTML = `(${CaseArchive.currentFilterState.areas.length})`;
		}

		// Locations
		if (CaseArchive.currentFilterState.locations.length === 0) {
			locationsDropdownToggleTextElement.innerHTML = '';
		} else {
			locationsDropdownToggleTextElement.innerHTML = `(${CaseArchive.currentFilterState.locations.length})`;
		}

		// Sectors
		if (CaseArchive.currentFilterState.sectors.length === 0) {
			sectorsDropdownToggleTextElement.innerHTML = '';
		} else {
			sectorsDropdownToggleTextElement.innerHTML = `(${CaseArchive.currentFilterState.sectors.length})`;
		}
	}
}

// Globals
CaseArchive.markers = [];
CaseArchive.map = null;
CaseArchive.markerClusterer = null;
CaseArchive.mapElement = datajs('case-archive-map-element');
CaseArchive.currentMarker = null;
CaseArchive.currentFilterState = null;
CaseArchive.infowindow = null;
